/*!

=========================================================
* Comprehend PT - Continue with Google
=========================================================

* Kicks off the server-side Google OAuth flow. There is no Google JS SDK here
* on purpose: the Chrome extension's MV3 CSP is `script-src 'self'`, which
* blocks accounts.google.com/gsi/client outright. Sending the browser to our
* own /v1/auth/google/start works identically in both surfaces.
*
* The backend redirects to /User/Home?sessionKey=..&sessionEmail=.. exactly the
* way the SAML callback does, and Home.js already consumes and scrubs those
* params - so nothing here needs to handle the session itself.

=========================================================

*/

/* global chrome */

import { useContext } from 'react';
import { Button } from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';

// The side panel can't follow a top-level redirect, so it opens a real tab and
// Login.js polls that tab's localStorage for the session - the same mechanism
// the CORA and Confluent SSO buttons use.
export const GOOGLE_SSO_STARTED = 'GOOGLE_SSO_STARTED';

const isCX = () => window.location.protocol === 'chrome-extension:';

// True when we're running inside an iframe — i.e. the embed widget mounted on
// an EMR page. Google refuses to run OAuth in a framed context (it returns a
// 403), so from here the flow must be opened in a top-level browser tab rather
// than navigating the iframe. Any cross-origin access check throwing also means
// we're framed, so treat that as embedded too.
const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

// Google's brand mark. Inlined rather than imported so this component has no
// asset dependency and renders identically in the extension bundle.
const GoogleMark = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: '10px', flexShrink: 0 }}
    aria-hidden="true"
  >
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
    />
  </svg>
);

/**
 * @param {string} label   Button text. Defaults to "Continue with Google".
 * @param {function} onStarted  Called right after the flow is kicked off, so a
 *                              parent (Login.js) can begin polling in the
 *                              extension. Optional.
 */
const GoogleSignInButton = ({ label = 'Continue with Google', onStarted }) => {
  const { apiClient } = useContext(GlobalContext);

  const handleClick = (e) => {
    e.preventDefault();
    const url = apiClient.googleSignInUrl();

    if (isCX()) {
      sessionStorage.setItem(GOOGLE_SSO_STARTED, 'true');
      chrome.tabs.create({ url });
    } else if (isEmbedded()) {
      const popup = window.open(
        url + '?embedOrigin=' + encodeURIComponent(window.location.origin),
        '_blank',
      );
      if (onStarted) onStarted({ popup });
      return;
    } else {
      window.location.href = url;
    }

    if (onStarted) onStarted();
  };

  return (
    <Button
      block
      color="white"
      type="button"
      onClick={handleClick}
      className="btn-neutral"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #dee2e6',
        fontWeight: 600,
        color: '#525f7f',
      }}
    >
      <GoogleMark />
      {label}
    </Button>
  );
};

export default GoogleSignInButton;
